import { capitalize } from '@roc/feature-utils';
import { LoanDocument } from '@roc/feature-types';
import { Service } from '@roc/feature-app-core';

const url = {
  EXTENSION_DOCUMENTS: '/api/v1/taskV2/getLoanTasks',
  DOCUMENT_DOWNLOAD: '/api/v1/loan/loanDocuments/downloadLoanDocument',
  DOCUMENT_DOWNLOAD_BY_NAME: '/api/v1/loan/loanDocuments/downloadDocumentByName',
  DOCUMENT_ADD_DOC: '/api/v1/taskV2/addLoanDocuments',
  DOCUMENT_SECTION_UPLOAD: '/api/v1/taskV2/uploadLoanDocuments',
  DOCUMENT_UPLOAD_CLOSING_DOCUMENTS: '/api/v1/taskV2/uploadClosingDocuments',
  DOCUMENT_UPLOAD: '/api/v1/taskV2/uploadTaskDocument',
  DOWNLOAD_BY_TASK_ID: '/api/v1/taskV2/taskDocuments/downloadByTaskId',
  GET_UNREAD_DOCUMENT_COMMENTS:
    '/api/v1/loan/documentCommunication/getUnreadDocumentComments',
  GET_UNREAD_DRAW_COMMENTS:
    '/api/v1/loan/documentCommunication/getAllUnreadDrawComments',
  DOCUMENT_HISTORY: '/api/v1/taskV2/getTaskDocumentHistory',
  DOWNLOAD_DOCUMENT_HISTORY: '/api/v1/taskV2/downloadTaskDocumentHistory',
  DOCUMENTS_PENDING: '/api/v1/loan/loanDocuments/getPendingLoanDocuments',
  GET_PROPERTY_DOCUMENT: '/api/v1/loan/loanDocuments/getPropertyDocument',
  DOWNLOAD_TERMSHEET: '/api/v1/loan/loanDocuments/downloadTermSheet',
  DOWNLOAD_DEAL_SUMMARY: '/api/v1/loan/loanDocuments/downloadDealSummary',
  UPDATE_LOAN_DOCUMENT: '/api/v1/document/formDocument/updateLoanDocument',
  DOWNLOAD_RATE_LOCK_AGREEMENT:
    '/api/v1/loan/loanDocuments/downloadRateLockAgreement',
  DOWNLOAD_ALL_FROM_SECTION:
    '/api/v1/taskV2/downloadAllFromSection',
  DOCUMENTS_ACCEPTED: '/api/v1/loan/loanDocuments/getBorrowerAcceptedLoanDocuments',
  PROPOSAL_DOCUMENTS: '/api/v1/loan/loanDocuments/getProposalDocumentsExternal',
  UPLOAD_PROPOSAL_DOCUMENT: '/api/v1/loan/loanDocuments/uploadProposalDocument',
  TRADE_DOCUMENTS: '/api/v1/assetm/trade/getTradeDocs',
  DOWNLOAD_BORROWER_PAST_DOCUMENTS: '/api/v1/loan/loanDocuments/downloadBorrowerPastLoanDocuments',
  EDIT_LOAN_DOCUMENT: '/nportal/rest/priv/loanDocs/editLoanDocument',
  DELETE_LOAN_DOCUMENT: '/nportal/rest/priv/loanDocs/deleteLoanDocument',
  DELETE_TASK_V2: "/api/v1/taskV2/deleteTaskByTaskId",
  EDIT_TASK_V2: "/api/v1/taskV2/editTaskTitle",
  DOCUMENT_HISTORY_WITH_USER_NAME: '/api/v1/loan/loanDocuments/getLoanDocumentHistoryWithUserFullName',
  UPLOAD_IVR_REPORT: "/api/v1/loan/loanDocuments/uploadInternalValuationReport",
  GET_CURRENT_CLOSING_CONDITION: '/api/v1/taskV2/getCurrentClosingCondition',
  UPLOAD_REAL_VAL_REPORT: "/api/v1/loan/loanDocuments/uploadRealValReport",
  GET_LOAN_TASKS: '/api/v1/taskV2/getLoanTasks',
  REMOVE_DOCUMENTS_BY_TASK_ID: '/api/v1/taskV2/taskDocuments/removeFilesByTaskId',
  UPDATE_TASK_DESCRIPTION: '/api/v1/taskV2/updateTaskDescription',
  REMOVE_FILES_BY_TASK_ID: '/api/v1/taskV2/taskDocuments/removeFilesByTaskId',
};

export class DocumentService extends Service {

  getUnreadDocumentComments(loanId: string, loanProcess: string) {
    return this.get(url.GET_UNREAD_DOCUMENT_COMMENTS, {
      loanId,
      loanProcess: capitalize(loanProcess),
    });
  }

  getUnreadDrawComments(loanId: string, drawId: string) {
    return this.get(
      `${url.GET_UNREAD_DRAW_COMMENTS}?loanId=${loanId}&drawId=${drawId}&loanProcess=Draws`
    );
  }

  getPendingDocuments(loanId: string, loanProcess: string) {
    return this.get(
      `${url.DOCUMENTS_PENDING}?loanId=${loanId}&loanProcess=${capitalize(
        loanProcess
      )}`
    );
  }

  getPropertyDocument(loanId: number, documentName: string, collateralId: number) {
    return this.get(
      `${url.GET_PROPERTY_DOCUMENT}?loanId=${loanId}&documentName=${documentName}&collateralId=${collateralId}`
    );
  }

  getProposalDocuments(loanId: string) {
    return this.get(`${url.PROPOSAL_DOCUMENTS}?loanId=${loanId}`);
  }

  getAcceptedDocuments(loanId: string, loanProcess: string) {
    return this.get(
      `${url.DOCUMENTS_ACCEPTED}?loanId=${loanId}&loanProcess=${capitalize(
        loanProcess
      )}`
    );
  }

  getExtensionDocuments(loanId: string, extensionId: string) {
    return this.get(
      `${url.EXTENSION_DOCUMENTS}/${loanId}?loanProcess=Extensions&type2=EXTENSION_ID&typeId2=${extensionId}&showAllTasks=true`
    );
  }

  addDocument(files: LoanDocument[]) {
    return this.post(`${url.DOCUMENT_ADD_DOC}?isInternal=false`, files);
  }

  uploadSectionDocument(file) {
    return this.post(url.DOCUMENT_SECTION_UPLOAD, file);
  }

  uploadClosingDocumentsCombined(formData, isInternal = false) {
    return this.post(`${url.DOCUMENT_UPLOAD_CLOSING_DOCUMENTS}?isInternal=${isInternal}`, formData);
  }

  uploadDocument(file, taskId: number, serviceMetaData?: any) {
    return this.post(`${url.DOCUMENT_UPLOAD}?taskId=${taskId}`, file, null, serviceMetaData);
  }

  uploadProposalDocument(file, loanDocId: number) {
    return this.post(`${url.UPLOAD_PROPOSAL_DOCUMENT}/${loanDocId}`, file);
  }

  downloadDocument(body) {
    return this.post(
      url.DOCUMENT_DOWNLOAD,
      body,
      {},
      { responseType: 'arraybuffer' }
    );
  }

  downloadDocumentByTaskId(taskId: number) {
    return this.get(
      `${url.DOWNLOAD_BY_TASK_ID}`,
      { taskId }
    );
  }

  downloadDocumentByName(body) {
    return this.post(
      url.DOCUMENT_DOWNLOAD_BY_NAME,
      body,
      {},
      { responseType: 'arraybuffer' }
    );
  }

  getDocumentHistory(documentId: number) {
    const endpointUrl = `${url.DOCUMENT_HISTORY}/${documentId}`;
    return this.get(endpointUrl, {});
  }

  downloadDocumentHistory(body) {
    return this.post(
      url.DOWNLOAD_DOCUMENT_HISTORY,
      body,
      {},
      { responseType: 'arraybuffer' }
    );
  }

  downloadTermSheet(loanId: number) {
    return this.get(
      url.DOWNLOAD_TERMSHEET,
      {
        loanId,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  downloadRateLockAgreement(loanId: number) {
    return this.get(
      url.DOWNLOAD_RATE_LOCK_AGREEMENT,
      {
        loanId,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  downloadDealSummary(loanId: number) {
    return this.get(
      url.DOWNLOAD_DEAL_SUMMARY,
      {
        loanId,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  updateDocument = (loanDocument: any) => {
    return this.put(url.UPDATE_LOAN_DOCUMENT, loanDocument);
  };

  downloadAllFromSection(category: string, loanId: number, type2: string, typeId2: number) {
    return this.get(
      url.DOWNLOAD_ALL_FROM_SECTION,
      {
        category,
        loanId,
        type2,
        type2Id: typeId2,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  downloadBorrowerPastLoanDocuments(borrowerId: number) {
    return this.get(
      url.DOWNLOAD_BORROWER_PAST_DOCUMENTS,
      { borrowerId },
      { responseType: 'arraybuffer' }
    );
  }

  editLoanDocument(loanDocument) {
    return this.put(url.EDIT_LOAN_DOCUMENT, { editedDocument: loanDocument });
  }

  editTask(loanDocument) {
    return this.put(`${url.EDIT_TASK_V2}/${loanDocument.taskId}`, { ...loanDocument } )
  }

  deleteDocument(loanDocument) {
    return this.delete(url.DELETE_TASK_V2, { taskId: loanDocument.taskId })
  }

  removeDocumentsByTaskId(taskId: number) {
    return this.delete(url.REMOVE_DOCUMENTS_BY_TASK_ID, { taskId });
  }

  getDocumentHistoryWithUserFullName(documentId: number, disableGlobalLoading: boolean) {
    const endpointUrl = `${url.DOCUMENT_HISTORY_WITH_USER_NAME}/${documentId}`;
    return this.get(endpointUrl, {}, { disableGlobalLoading });
  }

  uploadInternalValuationReport(loanId, propertyId, loanTaskId, report, disableGlobalLoading = false) {
    return this.post(`${url.UPLOAD_IVR_REPORT}/loanId/${loanId}/propertyId/${propertyId}/loanTaskId/${loanTaskId}`,
      report, null, { disableGlobalLoading }
    );
  }

  getCurrentClosingCondition(closingConditionId: number) {
    return this.get(`${url.GET_CURRENT_CLOSING_CONDITION}?closingConditionId=${closingConditionId}`);
  }

  uploadRealValReport(loanId, propertyId, report, disableGlobalLoading = false) {
    return this.post(`${url.UPLOAD_REAL_VAL_REPORT}/loanId/${loanId}/propertyId/${propertyId}`,
      report, null, { disableGlobalLoading }
    );
  }

  getLoanToDos(loanId, loanProcess, type2, typeId2, priorities, showAllTasks) {
    return this.get(`${url.GET_LOAN_TASKS}/${loanId}`, { loanProcess, type2, typeId2, priorities: priorities?.join(','), showAllTasks });
  }

  updateTaskDescription(taskId: number, description: string) {
    return this.put(url.UPDATE_TASK_DESCRIPTION, {taskId, description} );
  }

  removeFilesByTaskId(taskId: number) {
    return this.delete(url.REMOVE_FILES_BY_TASK_ID, { taskId });
  }
}
